import { Expense, RightEnum, Vendor } from "@finway-group/shared/lib/models"
import { Select, Tooltip } from "antd"
import React from "react"
import { AlertTriangle } from "react-feather"
import { useTranslation } from "react-i18next"
import { generatePath, Link } from "react-router-dom"
import { WARNING_ORANGE } from "Shared/config/consts"
import { RoutePathEnum } from "Shared/router/RoutePath.enum"
import { AuthzService } from "Shared/services"
import { checkIsVendorPaymentDataMismatch, getBicOptions, getIbanOptions, isInvoiceBicDifferent, isInvoiceIbanDifferent } from "Shared/utils/expense.utils"
import { getIBANPrintFormat } from "Shared/utils/helper.utils"
import { getVendorLocationLabel, UserCreditor } from "Shared/utils/vendor.utils"
import { OcrIcon } from "./icons/OcrIcon"

// Generic interface for documents that have vendor/creditor data
interface DocumentWithCreditor {
    vendor?: {
        _id: string
        name?: string
        creditorNumber?: string
        vatNumber?: string
        iban?: string
        bic?: string
        contactPersonName?: string
    }
    creditorUser?: {
        _id: string
        firstName?: string
        lastName?: string
        email?: string
        location?: string
        iban?: string
        bic?: string
        creditorNumber?: string
    }
}

interface CreditorDataListProps<T extends DocumentWithCreditor> {
    documentCreditor: Vendor | UserCreditor
    document: T
    isUpdating: boolean
    canEditItems?: boolean
    onVendorDataUpdate?: (patchVendorData: Partial<Pick<Vendor, "iban" | "bic">>) => void
}

export const CreditorDataList = <T extends DocumentWithCreditor>({
    documentCreditor,
    document,
    isUpdating,
    canEditItems = false,
    onVendorDataUpdate,
}: CreditorDataListProps<T>) => {
    const { t } = useTranslation()

    if (!documentCreditor) {
        return <></>
    }

    if (documentCreditor instanceof Vendor) {
        const documentVendor = document.vendor

        if (!documentVendor) {
            return null
        }

        const vendor = documentCreditor as Vendor

        const vendorLocationLabel = getVendorLocationLabel(documentVendor)

        // Only show payment data options if editing is allowed and document supports it
        const showPaymentDataOptions = canEditItems && onVendorDataUpdate

        let ibanOptions: Array<{ value: string | undefined; label: string; isInvoiceIban: boolean }> = []
        let bicOptions: Array<{ value: string | undefined; label: string; isInvoiceBic: boolean }> = []
        let isVendorPaymentDataMismatch = false

        if (showPaymentDataOptions) {
            // Cast to Expense for these utility functions
            const expense = document as unknown as Expense
            ibanOptions = getIbanOptions(expense)
            bicOptions = getBicOptions(expense)
            isVendorPaymentDataMismatch = checkIsVendorPaymentDataMismatch(expense)
        }

        return (
            <ul className="description-list pb-10 overflow-hidden">
                <li>
                    <span className="title">{t("label:name")}</span>
                    <span className="text truncate max-w-250">
                        <Link to={generatePath(RoutePathEnum.VENDOR_DETAIL_PAGE, { id: documentVendor?._id! })}>{documentVendor?.name}</Link>
                    </span>
                </li>
                <li>
                    <span className="title">{t("label:creditor_number")}</span>
                    <span className="text truncate max-w-250">{documentVendor?.creditorNumber ?? "-"}</span>
                </li>
                <li>
                    <span className="title">{t("label:description")}</span>
                    <span className="text truncate max-w-250">{vendor.description}</span>
                </li>
                <li>
                    <span className="title">{t("label:location")}</span>
                    <span className="text truncate max-w-250">{vendorLocationLabel}</span>
                </li>
                <li>
                    <span className="title">{t("label:vat_number")}</span>
                    <span className="text">{documentVendor?.vatNumber ?? "-"}</span>
                </li>
                <li>
                    <span className="title ">{t("label:iban")}</span>
                    <div className="text">
                        {showPaymentDataOptions && isVendorPaymentDataMismatch && ibanOptions.length > 0 ? (
                            <div className="flex flex-row items-center ">
                                {isInvoiceIbanDifferent(document as unknown as Expense) && (
                                    <div>
                                        <Tooltip title={t("info:ocr.iban_mismatch")}>
                                            <AlertTriangle fill={WARNING_ORANGE} size={24} stroke="#FFFFFF" className="mr-4" />
                                        </Tooltip>
                                    </div>
                                )}
                                <Select
                                    defaultValue={documentVendor?.iban ?? t("missing:select_option_no_value")}
                                    value={documentVendor?.iban ?? ""}
                                    dropdownMatchSelectWidth={false}
                                    onChange={(value) => onVendorDataUpdate?.({ iban: value })}
                                    className="overflow-auto min-w-120"
                                    disabled={isUpdating}
                                >
                                    {ibanOptions.map((option) => (
                                        <Select.Option key={option.value} value={option.value ?? ""} label={option.label} className="overflow-auto">
                                            <div className="flex flex-row items-center">
                                                <span className="overflow-auto"> {option.label}</span>
                                                {option.isInvoiceIban && <OcrIcon className="pl-6" />}
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        ) : documentVendor?.iban ? (
                            getIBANPrintFormat(documentVendor.iban)
                        ) : (
                            t("missing:select_option_no_value")
                        )}
                    </div>
                </li>
                <li>
                    <span className="title">{t("label:bic")}</span>
                    <div className="text">
                        {showPaymentDataOptions && isVendorPaymentDataMismatch && bicOptions.length > 0 ? (
                            <div className="flex flex-row items-center ">
                                {isInvoiceBicDifferent(document as unknown as Expense) && (
                                    <div>
                                        <Tooltip title={t("info:ocr.bic_mismatch")}>
                                            <AlertTriangle fill={WARNING_ORANGE} size={24} stroke="#FFFFFF" className="mr-4" />
                                        </Tooltip>
                                    </div>
                                )}
                                <Select
                                    defaultValue={documentVendor?.bic ?? t("missing:select_option_no_value")}
                                    value={documentVendor?.bic ?? ""}
                                    dropdownMatchSelectWidth={false}
                                    onChange={(value) => onVendorDataUpdate?.({ bic: value })}
                                    className="overflow-auto min-w-120"
                                    disabled={isUpdating}
                                >
                                    {bicOptions.map((option) => (
                                        <Select.Option key={option.value} value={option.value ?? ""} label={option.label} className="overflow-auto">
                                            <div className="flex flex-row items-center">
                                                <span className="overflow-auto"> {option.label}</span>
                                                {option.isInvoiceBic && <OcrIcon className="pl-6" />}
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        ) : (
                            documentVendor?.bic || t("missing:select_option_no_value")
                        )}
                    </div>
                </li>
                <li>
                    <span className="title">{t("label:contact_person")}</span>
                    <span className="text truncate max-w-250">{vendor.contactPersonName || "-"}</span>
                </li>
            </ul>
        )
    }

    const documentEmployee = document.creditorUser

    return (
        <ul className="description-list pb-10 overflow-auto">
            <li>
                <span className="title">{t("label:name")}</span>
                <span className="text truncate max-w-250">
                    <Link to={generatePath(RoutePathEnum.EMPLOYEE_DETAIL_PAGE, { id: documentEmployee?._id! })}>
                        {documentEmployee?.firstName} {documentEmployee?.lastName}
                    </Link>
                </span>
            </li>
            <li>
                <span className="title">{t("label:email")}</span>
                <span className="text truncate max-w-250">{documentEmployee?.email}</span>
            </li>
            <li>
                <span className="title">{t("label:location")}</span>
                <span className="text truncate max-w-250">{documentEmployee?.location}</span>
            </li>
            <li>
                <span className="title">{t("label:iban")}</span>
                <span className="text">{documentEmployee?.iban || "-"}</span>
            </li>
            <li>
                <span className="title">{t("label:bic")}</span>
                <span className="text">{documentEmployee?.bic || "-"}</span>
            </li>
            {AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ) && (
                <li>
                    <span className="title">{t("label:creditor_number")}</span>
                    <span className="text truncate max-w-250">{documentEmployee?.creditorNumber || "-"}</span>
                </li>
            )}
        </ul>
    )
}
